import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import './CourseModal.css';

const candleData = [
  { date: 'Jan', open: 180, close: 195, high: 202, low: 175 },
  { date: 'Feb', open: 195, close: 188, high: 204, low: 183 },
  { date: 'Mar', open: 188, close: 212, high: 216, low: 185 },
  { date: 'Apr', open: 212, close: 206, high: 218, low: 200 },
  { date: 'May', open: 206, close: 228, high: 232, low: 203 },
  { date: 'Jun', open: 228, close: 220, high: 234, low: 216 },
  { date: 'Jul', open: 220, close: 244, high: 248, low: 217 },
  { date: 'Aug', open: 244, close: 238, high: 250, low: 233 },
  { date: 'Sep', open: 238, close: 258, high: 263, low: 235 },
  { date: 'Oct', open: 258, close: 250, high: 265, low: 246 },
  { date: 'Nov', open: 250, close: 272, high: 278, low: 247 },
  { date: 'Dec', open: 272, close: 268, high: 280, low: 262 },
];

const barData = [
  { name: 'Revenue', Q1: 42, Q2: 58, Q3: 65, Q4: 78 },
  { name: 'EBITDA',  Q1: 18, Q2: 24, Q3: 28, Q4: 34 },
  { name: 'PAT',     Q1: 10, Q2: 14, Q3: 17, Q4: 22 },
  { name: 'EPS',     Q1: 8,  Q2: 11, Q3: 14, Q4: 18 },
];

const areaData = [
  { month: 'Jan', value: 10000 }, { month: 'Feb', value: 10420 },
  { month: 'Mar', value: 10180 }, { month: 'Apr', value: 10950 },
  { month: 'May', value: 11400 }, { month: 'Jun', value: 11100 },
  { month: 'Jul', value: 12200 }, { month: 'Aug', value: 12800 },
  { month: 'Sep', value: 13500 }, { month: 'Oct', value: 13100 },
  { month: 'Nov', value: 14200 }, { month: 'Dec', value: 15000 },
];

const CandleChart = () => {
  const minVal = 165, maxVal = 290, range = maxVal - minVal;
  const svgH = 200, svgW = 600, padTop = 10, padBot = 20;
  const chartH = svgH - padTop - padBot;
  const toY = (v) => padTop + ((maxVal - v) / range) * chartH;
  const slotW = svgW / candleData.length;

  return (
    <>
      <div className="cm-chart-live">
        <span className="cm-live-dot"></span>
        Market Simulation
      </div>
      <div className="cm-chart-wrap">
        <div className="cm-chart-labels">
          {['290','260','230','200','170'].map((v,i) => <span key={i}>{v}</span>)}
        </div>
        <div className="cm-chart-main">
          <svg width="100%" height="200" viewBox={`0 0 ${svgW} ${svgH}`} preserveAspectRatio="none">
            {[290,260,230,200,170].map(v => (
              <line key={v} x1="0" y1={toY(v)} x2={svgW} y2={toY(v)}
                stroke="rgba(160,120,40,0.1)" strokeWidth="1"/>
            ))}
            {candleData.map((d, i) => {
              const isUp = d.close >= d.open;
              const color = isUp ? '#4ecb71' : '#e84040';
              const x = i * slotW + slotW / 2;
              const bW = slotW * 0.45;
              return (
                <g key={i} className="cm-candle">
                  <line x1={x} y1={toY(d.high)} x2={x} y2={toY(d.low)} stroke={color} strokeWidth="1.5"/>
                  <rect
                    x={x - bW / 2} y={toY(Math.max(d.open, d.close))}
                    width={bW} height={Math.max(Math.abs(toY(d.open) - toY(d.close)), 2)}
                    fill={color} rx="1" opacity="0.9"
                  />
                </g>
              );
            })}
          </svg>
          <div className="cm-chart-xaxis">
            {candleData.map((d,i) => <span key={i}>{d.date}</span>)}
          </div>
        </div>
      </div>
    </>
  );
};

const BarTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div className="cm-tooltip">
        <div className="cm-tooltip-date">{label}</div>
        {payload.map((p,i) => (
          <div className="cm-tooltip-row" key={i}>
            <span>{p.name}</span>
            <span style={{color:'var(--gold)'}}>₹{p.value}Cr</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const AreaTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    return (
      <div className="cm-tooltip">
        <div className="cm-tooltip-date">{payload[0]?.payload?.month}</div>
        <div className="cm-tooltip-row">
          <span>Portfolio</span>
          <span style={{color:'var(--gold)'}}>₹{payload[0]?.value?.toLocaleString()}</span>
        </div>
      </div>
    );
  }
  return null;
};

const chartConfigs = {
  candle: {
    title: "Sample Price Action — What you'll learn to read",
    sub: 'NIFTY 50 · Monthly Candlestick · Simulated Data',
    note: "📊 You'll learn to read charts like this — identifying patterns, trends, support & resistance to make confident trading decisions.",
    legend: [
      { color: '#4ecb71', label: '▲ Bullish Candle' },
      { color: '#e84040', label: '▼ Bearish Candle' },
    ],
  },
  bar: {
    title: "Company Financials — What you'll learn to analyse",
    sub: 'Sample Co. · Quarterly P&L · Simulated Data (₹ Cr)',
    note: "📊 You'll learn to read financial statements like this — analysing revenue trends, margins and valuations to pick strong stocks.",
    legend: [
      { color: '#a07828', label: '▬ Revenue' },
      { color: '#4ecb71', label: '▬ EBITDA' },
      { color: '#5b9bd5', label: '▬ PAT' },
    ],
  },
  area: {
    title: "SIP Growth Simulation — What you'll learn to plan",
    sub: 'SIP ₹10,000/month · 12 months · Simulated Growth',
    note: "📊 You'll learn to plan investments like this — understanding compounding, SIP growth and portfolio building for long-term wealth.",
    legend: [{ color: '#a07828', label: '▬ Portfolio Value' }],
  },
};

function CourseModal({ course, onClose }) {
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  if (!course) return null;

  const chartType = course.chartType || 'candle';
  const config = chartConfigs[chartType];

  const renderChart = () => {
    if (chartType === 'candle') return <CandleChart />;
    if (chartType === 'bar') {
      return (
        <div className="cm-chart-wrap cm-chart-wrap-pad">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={barData} margin={{top:10,right:10,left:-15,bottom:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(160,120,40,0.1)" vertical={false}/>
              <XAxis dataKey="name" tick={{fill:'var(--text-muted)',fontSize:10}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fill:'var(--text-muted)',fontSize:10}} axisLine={false} tickLine={false}/>
              <Tooltip content={<BarTooltip/>}/>
              <Bar dataKey="Q1" fill="rgba(160,120,40,0.4)" radius={[2,2,0,0]}/>
              <Bar dataKey="Q2" fill="rgba(160,120,40,0.65)" radius={[2,2,0,0]}/>
              <Bar dataKey="Q3" fill="rgba(78,203,113,0.55)" radius={[2,2,0,0]}/>
              <Bar dataKey="Q4" fill="rgba(78,203,113,0.8)" radius={[2,2,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
          <div style={{display:'flex',gap:12,justifyContent:'center',marginTop:6}}>
            {['Q1','Q2','Q3','Q4'].map((q,i) => (
              <span key={i} style={{fontSize:9,color:'var(--text-muted)'}}>
                <span style={{color: i<2 ? 'var(--gold)':'#4ecb71'}}>■ </span>{q}
              </span>
            ))}
          </div>
        </div>
      );
    }
    if (chartType === 'area') {
      return (
        <div className="cm-chart-wrap cm-chart-wrap-pad">
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={areaData} margin={{top:10,right:10,left:-10,bottom:0}}>
              <defs>
                <linearGradient id="areaGold" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#a07828" stopOpacity={0.25}/>
                  <stop offset="95%" stopColor="#a07828" stopOpacity={0.02}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(160,120,40,0.1)" vertical={false}/>
              <XAxis dataKey="month" tick={{fill:'var(--text-muted)',fontSize:10}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fill:'var(--text-muted)',fontSize:9}} axisLine={false} tickLine={false}
                tickFormatter={v => `₹${(v/1000).toFixed(0)}k`}/>
              <Tooltip content={<AreaTooltip/>}/>
              <Area type="monotone" dataKey="value" stroke="var(--gold)" strokeWidth={2}
                fill="url(#areaGold)" dot={{fill:'var(--gold)',r:3}} activeDot={{r:5}}/>
            </AreaChart>
          </ResponsiveContainer>
        </div>
      );
    }
  };

  return (
    <div className="cm-overlay" onClick={onClose}>
      <div className="cm-modal" onClick={e => e.stopPropagation()}>
        <button className="cm-close" onClick={onClose}>✕</button>
        <div className="cm-content">

          <div className="cm-header">
            <div className="cm-header-top">
              <span className="cm-badge">{course.badge}</span>
              <span className="cm-level">{course.level}</span>
            </div>
            <h2 className="cm-title">{course.title}</h2>
            <p className="cm-desc">{course.desc}</p>
            <div className="cm-meta">
              <span>⏱ {course.hours}</span>
              <span>🎯 {course.type}</span>
              <span>🗣 {course.lang}</span>
              <span className="cm-price-inline">{course.price}</span>
            </div>
          </div>

          <div className="cm-chart-section">
            <div className="cm-chart-header">
              <div className="cm-chart-title">{config.title}</div>
              <div className="cm-chart-sub">{config.sub}</div>
            </div>
            <div className="cm-chart-legend">
              {config.legend.map((l,i) => (
                <span key={i} style={{color: l.color, fontSize:'10px'}}>{l.label}</span>
              ))}
            </div>
            {renderChart()}
            <p className="cm-chart-note">{config.note}</p>
          </div>

          <div className="cm-learn">
            <div className="cm-section-tag">Course Syllabus</div>
            <div className="cm-learn-grid">
              {(course.syllabus || []).map((item, i) => (
                <div className="cm-learn-item" key={i}>
                  <span className="cm-learn-num">{String(i+1).padStart(2,'0')}</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="cm-cta">
            <div className="cm-cta-left">
              <div className="cm-cta-price">{course.price}</div>
              <div className="cm-cta-duration">{course.hours} · {course.lang}</div>
            </div>
            <Link
              to={`/enquiry?course=${encodeURIComponent(course.title)}&badge=${encodeURIComponent(course.badge)}&price=${encodeURIComponent(course.price)}`}
              className="cm-cta-btn" onClick={onClose}
            >
              Enrol Now →
            </Link>
          </div>

        </div>
      </div>
    </div>
  );
}

export default CourseModal;